import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    Badge,
    Progress,
    IconButton,
    useToast,
    Spinner,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useColorModeValue,
    Icon,
    Select,
    Tag,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdCheckCircle, MdError, MdUploadFile, MdAutoAwesome, MdDelete, MdAdd, MdDownload } from 'react-icons/md';
import {
    useSubmittalSlots,
    useCreateSubmittalSlot,
    useUpdateSubmittalSlot,
    useDeleteSubmittalSlot,
    useAutofillSubmittalSlot,
    useDocumentTemplates
} from '../../services/submittals.hooks';

interface BidSubmittalsTabProps {
    pursuitId: number;
}

export function BidSubmittalsTab({ pursuitId }: BidSubmittalsTabProps) {
    const toast = useToast();
    const panelBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    // Data Hooks
    const { data: slots = [], isLoading } = useSubmittalSlots(pursuitId);
    const { data: templates = [] } = useDocumentTemplates();
    const createSlotMutation = useCreateSubmittalSlot();

    const [isCreating, setIsCreating] = useState(false);

    const handleAddSlot = () => {
        setIsCreating(true);
        createSlotMutation.mutate({
            pursuitId,
            data: { name: "New Submittal Document", status: "empty" }
        }, {
            onSuccess: () => {
                toast({ title: "Slot created", status: "success", duration: 2000 });
                setIsCreating(false);
            },
            onError: () => setIsCreating(false)
        });
    };

    // Calculate Progress
    const total = slots.length;
    const completed = slots.filter((s: any) => s.status === 'ready' || s.status === 'submitted').length;
    const progress = total > 0 ? (completed / total) * 100 : 0;

    return (
        <Box display="flex" flexDirection="column" gap={6}>

            {/* Header & Actions */}
            <Flex
                justify="space-between"
                align="center"
                p={6}
                bg={panelBg}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                shadow="sm"
                wrap="wrap"
                gap={4}
            >
                <Box>
                    <Heading size="md" mb={1}>Bid Submittals</Heading>
                    <Text fontSize="sm" color="gray.500">Assemble and auto-fill the required documents for your bid package.</Text>
                </Box>
                <HStack>
                    <Button
                        leftIcon={<MdAdd />}
                        colorScheme="blue"
                        onClick={handleAddSlot}
                        isLoading={isCreating}
                    >
                        Add Document Slot
                    </Button>
                </HStack>
            </Flex>

            {/* Progress */}
            {slots.length > 0 && (
                <Box px={2}>
                    <HStack justify="space-between" mb={2} fontSize="sm">
                        <Text fontWeight="bold">Assembly Progress</Text>
                        <Text>{Math.round(progress)}% Ready</Text>
                    </HStack>
                    <Progress value={progress} colorScheme={progress === 100 ? 'green' : 'blue'} borderRadius="full" size="sm" />
                </Box>
            )}

            {/* Document Slots */}
            <VStack spacing={3} align="stretch">
                {isLoading ? (
                    <Flex justify="center" p={10}><Spinner /></Flex>
                ) : slots.length === 0 ? (
                    <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        p={10}
                        bg={panelBg}
                        borderRadius="xl"
                        borderStyle="dashed"
                        borderWidth="2px"
                        borderColor="gray.300"
                    >
                        <Icon as={MdUploadFile} boxSize={10} color="gray.300" mb={3} />
                        <Text color="gray.500" fontWeight="medium">No submittal slots created.</Text>
                        <Text fontSize="sm" color="gray.400" mb={4}>Click "Add Document Slot" to start assembling your bid.</Text>
                    </Flex>
                ) : (
                    slots.map((slot: any) => (
                        <SubmittalSlotRow
                            key={slot.id}
                            slot={slot}
                            templates={templates}
                        />
                    ))
                )}
            </VStack>
        </Box>
    );
}

// Sub-component for individual row
function SubmittalSlotRow({ slot, templates }: { slot: any; templates: any[] }) {
    const bg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    const updateSlotMutation = useUpdateSubmittalSlot();
    const deleteSlotMutation = useDeleteSubmittalSlot();
    const autofillMutation = useAutofillSubmittalSlot();

    const handleStatusChange = (newStatus: string) => {
        updateSlotMutation.mutate({ id: slot.id, data: { status: newStatus } });
    };

    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this submittal slot?')) {
            deleteSlotMutation.mutate(slot.id);
        }
    };

    const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        updateSlotMutation.mutate({
            id: slot.id,
            data: { template_id: val ? parseInt(val) : null }
        });
    };

    const handleAutofill = () => {
        autofillMutation.mutate(slot.id);
    };

    const statusColors: Record<string, string> = {
        empty: 'gray',
        drafting: 'purple',
        ready: 'green',
        submitted: 'blue'
    };

    return (
        <Box
            p={4}
            bg={bg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            borderLeftWidth="4px"
            borderLeftColor={`${statusColors[slot.status]}.400`}
            shadow="sm"
            transition="all 0.2s"
            _hover={{ shadow: 'md' }}
        >
            <Flex gap={4} align="center" wrap="wrap">

                {/* Status indicator */}
                <Menu>
                    <MenuButton
                        as={IconButton}
                        icon={
                            slot.status === 'ready' ? <MdCheckCircle /> :
                                slot.status === 'drafting' ? <Spinner size="xs" /> :
                                    slot.status === 'empty' ? <MdError /> : <MdCheckCircle />
                        }
                        variant="ghost"
                        colorScheme={statusColors[slot.status]}
                        size="sm"
                    />
                    <MenuList>
                        <MenuItem onClick={() => handleStatusChange('empty')}>Mark as Empty</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('ready')}>Mark as Ready</MenuItem>
                        <MenuItem onClick={() => handleStatusChange('submitted')}>Mark as Submitted</MenuItem>
                    </MenuList>
                </Menu>

                <Box flex={1} minW="200px">
                    <HStack mb={1}>
                        <Badge colorScheme={statusColors[slot.status]} fontSize="xx-small">
                            {slot.status.toUpperCase()}
                        </Badge>
                        {slot.compliance_item_id && <Tag size="sm" colorScheme="orange">Requirement Linked</Tag>}
                    </HStack>
                    <Text fontWeight="medium" fontSize="sm">
                        {slot.name}
                    </Text>
                </Box>

                {/* Template Selection */}
                <Box w={{ base: "100%", md: "250px" }}>
                    <Select
                        size="sm"
                        placeholder="Select Template..."
                        value={slot.template_id || ''}
                        onChange={handleTemplateChange}
                    >
                        {templates.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </Select>
                </Box>

                {/* Actions */}
                <HStack spacing={2}>
                    <Button
                        size="sm"
                        leftIcon={<MdAutoAwesome />}
                        colorScheme="purple"
                        variant="outline"
                        isDisabled={!slot.template_id || slot.status === 'drafting'}
                        isLoading={autofillMutation.isPending}
                        onClick={handleAutofill}
                    >
                        AI Auto-Fill
                    </Button>
                    <IconButton
                        aria-label="Download"
                        icon={<MdDownload />}
                        size="sm"
                        variant="ghost"
                        isDisabled={slot.status === 'empty' || slot.status === 'drafting'}
                    />
                    <IconButton
                        aria-label="Delete"
                        icon={<MdDelete />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={handleDelete}
                    />
                </HStack>
            </Flex>
        </Box>
    );
}
